import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { routineTimeKey } from '@drbell/core';
import {
  api, ApiError, DEFAULT_MEAL_TIMES, type MealTimes, type MedicationEntry, type PatientDetail, type PatientSummary,
  type RoutineTime,
} from '../api.ts';
import { MEAL_NAME, MEALS, outOfOrder, sameTimes, spokenTime, todayIn } from '../self-service.ts';
import { TimeMedicines } from './RoutineLinks.tsx';

/**
 * A person's daily routine — the phone's Daily routine (apps/mobile
 * Routine.tsx), on the website.
 *
 *  - **Meal times**: Breakfast, Lunch, Dinner, Bedtime. "1-0-1 after food"
 *    is scheduled from these, so saving moves every reminder tied to a
 *    meal (`PUT …/meal-times`). Out of order is a warning, never a block: a
 *    night shift genuinely eats breakfast at 21:00.
 *  - **Other times of the day**: up to six of the person's own — "Evening
 *    tea, 17:00" — added, renamed, moved and deleted as on the phone.
 *    Deleting one medicines are tied to asks where they go: another time,
 *    or the same clock times every day. Nothing is dropped.
 *  - **The medicines at each time**, with Link and Unlink (RoutineLinks).
 *
 * Changing the routine is for whoever may edit the record (`patient.edit`);
 * linking medicines for whoever may edit medicines. Everyone else reads it.
 */
const MAX_OWN = 6;

export function Routine({ patient, onOpenMedicines }: { patient: PatientSummary; onOpenMedicines: () => void }) {
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [medicines, setMedicines] = useState<MedicationEntry[] | null>(null);
  const [meals, setMeals] = useState<MealTimes>({ ...DEFAULT_MEAL_TIMES });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<Destructive | null>(null);

  const loadMedicines = useCallback(async (d: PatientDetail) => {
    // A viewer may not read the medicines: the routine is shown without them, not refused.
    if (!d.access.capabilities.includes('medication.read')) return setMedicines(null);
    try {
      setMedicines((await api.get<{ medications: MedicationEntry[] }>(`/api/patients/${patient.id}/medications?kind=medicine`)).medications);
    } catch {
      setMedicines(null);
    }
  }, [patient.id]);

  const load = useCallback(async () => {
    try {
      const d = await api.get<PatientDetail>(`/api/patients/${patient.id}`);
      setDetail(d);
      setMeals({ ...(d.mealTimes ?? DEFAULT_MEAL_TIMES) });
      setError(null);
      await loadMedicines(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patient.id, loadMedicines]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!detail) return <p className="text-(--muted)">Loading…</p>;

  const name = detail.patient.displayName;
  const canEdit = detail.access.capabilities.includes('patient.edit');
  const canLink = detail.access.capabilities.includes('medication.edit');
  const saved = { ...(detail.mealTimes ?? DEFAULT_MEAL_TIMES) };
  const changed = !sameTimes(meals, saved);
  const own = detail.routineTimes ?? [];
  const today = todayIn(detail.patient.timezone);

  const takenAt = (key: Parameters<typeof TimeMedicines>[0]['anchorKey'], timeName: string) =>
    medicines ? (
      <TimeMedicines
        patientId={patient.id}
        anchorKey={key}
        timeName={timeName}
        entries={medicines}
        ownTimes={own}
        today={today}
        canEdit={canLink}
        onAsk={setAsk}
        onOpen={onOpenMedicines}
        onChanged={() => loadMedicines(detail)}
      />
    ) : null;

  const saveMeals = async () => {
    setBusy(true);
    try {
      await api.put(`/api/patients/${patient.id}/meal-times`, meals);
      toast.success('Meal times saved', 'Every reminder that follows a meal has moved to match.');
      await load();
    } catch (e) {
      toast.error('Could not save the meal times', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold">{name}&rsquo;s routine</h2>
        {!canEdit && (
          <p className="mt-1 rounded-lg border border-(--brand) bg-(--brand-soft) px-3 py-2 text-sm">
            Only someone who manages {name}&rsquo;s medicines can change this.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-(--muted)">Meal times</h3>
        <p className="mt-1 text-sm text-(--muted)">
          Doses written as 1-0-1 or &ldquo;after food&rdquo; follow these. Set them to when {name} actually eats.
        </p>
        <ul className="mt-3 space-y-3">
          {MEALS.map((meal) => (
            <li key={meal} className="rounded-xl border border-(--line) p-3">
              <label className="flex flex-wrap items-center gap-3">
                <span className="min-w-24 font-medium">{MEAL_NAME[meal]}</span>
                {canEdit ? (
                  <input
                    type="time"
                    value={meals[meal]}
                    onChange={(e) => e.target.value && setMeals({ ...meals, [meal]: e.target.value })}
                    className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3"
                  />
                ) : (
                  <span>{spokenTime(meals[meal])}</span>
                )}
              </label>
              {takenAt(meal, MEAL_NAME[meal])}
            </li>
          ))}
        </ul>
        {canEdit && changed && (
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button onClick={() => setMeals(saved)} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Cancel</button>
            <button
              disabled={busy}
              onClick={() => {
                if (!outOfOrder(meals)) return void saveMeals();
                setAsk({
                  title: 'Meals out of order',
                  body: 'These times do not run breakfast, lunch, dinner, bedtime. That is fine for a night shift. Save anyway?',
                  confirmLabel: 'Save anyway',
                  tone: 'primary',
                  onConfirm: saveMeals,
                });
              }}
              className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Saving…' : 'Save meal times'}
            </button>
          </div>
        )}
      </div>

      <OwnTimes
        patientId={patient.id}
        name={name}
        times={own}
        canEdit={canEdit}
        onAsk={setAsk}
        onChanged={load}
        below={(t) => takenAt(routineTimeKey(t.id), t.name)}
      />

      {ask && <Confirm {...ask} onCancel={() => setAsk(null)} />}
    </section>
  );
}

/** Up to six of the person's own named times, as the phone's RoutineTimesField. */
function OwnTimes({ patientId, name, times, canEdit, onAsk, onChanged, below }: {
  patientId: string;
  name: string;
  times: readonly RoutineTime[];
  canEdit: boolean;
  onAsk: (ask: Destructive) => void;
  onChanged: () => Promise<void>;
  below: (t: RoutineTime) => ReactNode;
}) {
  const [drafts, setDrafts] = useState<Record<string, { name: string; time: string }>>({});
  const [adding, setAdding] = useState<{ name: string; time: string } | null>(null);
  const [removing, setRemoving] = useState<{ time: RoutineTime; medicines: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const problem = (e: unknown) =>
    e instanceof ApiError && e.code === 'routine_time_name'
      ? 'Give it a name of 1 to 30 characters, different from the other times.'
      : e instanceof ApiError && e.code === 'routine_time_limit'
        ? `Up to ${MAX_OWN} other times of the day.`
        : e instanceof Error ? e.message : String(e);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (e) {
      toast.error('Other times of the day', problem(e));
    } finally {
      setBusy(false);
    }
  };

  /** Without a choice first: a time medicines are tied to is refused with their names, and the person chooses. */
  const remove = async (time: RoutineTime, removal?: { medicines: 'move'; to: string } | { medicines: 'keep' }) => {
    try {
      await api.del(`/api/patients/${patientId}/routine-times/${time.id}`, removal);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'routine_time_in_use') {
        const medicines = (e.body?.medicines as Array<{ name: string }> | undefined) ?? [];
        setRemoving({ time, medicines: medicines.map((m) => m.name) });
        return;
      }
      throw e;
    }
    setRemoving(null);
    toast.success(`${time.name} deleted.`);
    await onChanged();
  };

  const input = 'min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3';

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-(--muted)">Other times of the day</h3>
      <p className="mt-1 text-sm text-(--muted)">
        Times of {name}&rsquo;s day besides meals, like Evening tea. A medicine can be taken before, with or after
        one, as with a meal.
      </p>

      <ul className="mt-3 space-y-3">
        {times.map((t) => {
          const d = drafts[t.id] ?? { name: t.name, time: t.time };
          const changed = d.name !== t.name || d.time !== t.time;
          const set = (next: Partial<typeof d>) => setDrafts((all) => ({ ...all, [t.id]: { ...d, ...next } }));
          return (
            <li key={t.id} className="rounded-xl border border-(--line) p-3">
              {canEdit ? (
                <div className="flex flex-wrap gap-2">
                  <input
                    aria-label="Name of the time"
                    value={d.name}
                    maxLength={30}
                    onChange={(e) => set({ name: e.target.value })}
                    className={`${input} min-w-40 flex-1`}
                  />
                  <input
                    aria-label={`${t.name}, time`}
                    type="time"
                    value={d.time}
                    onChange={(e) => e.target.value && set({ time: e.target.value })}
                    className={input}
                  />
                  {changed ? (
                    <>
                      <button disabled={busy} onClick={() => setDrafts(({ [t.id]: _x, ...rest }) => rest)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Cancel</button>
                      <button
                        disabled={busy}
                        onClick={() => void run(async () => {
                          const r = await api.put<{ rescheduling: boolean; routineTime: RoutineTime }>(
                            `/api/patients/${patientId}/routine-times/${t.id}`,
                            { ...(d.name !== t.name ? { name: d.name } : {}), ...(d.time !== t.time ? { time: d.time } : {}) },
                          );
                          setDrafts(({ [t.id]: _x, ...rest }) => rest);
                          toast.success(r.routineTime.name, r.rescheduling
                            ? `Saved. Every reminder tied to ${r.routineTime.name} has moved to match.`
                            : 'Saved.');
                          await onChanged();
                        })}
                        className="min-h-11 rounded-lg bg-(--accent) px-3 text-sm font-medium text-white disabled:opacity-40"
                      >
                        Save
                      </button>
                    </>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() => onAsk({
                        title: `Delete ${t.name}?`,
                        body: `${t.name}, ${spokenTime(t.time)}, is removed from ${name}’s day.`,
                        reassurance: 'If medicines are taken at it, you choose next where they go. Nothing is dropped.',
                        confirmLabel: 'Delete',
                        onConfirm: () => remove(t),
                      })}
                      className="min-h-11 rounded-lg border border-(--line) px-3 text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>
              ) : (
                <p className="font-medium">{t.name} <span className="font-normal text-(--muted)">· {spokenTime(t.time)}</span></p>
              )}

              {below(t)}

              {removing?.time.id === t.id && (
                <div role="alert" className="mt-2 rounded-lg bg-(--accent-soft) p-3 text-sm">
                  <p>
                    Medicines are taken at {t.name}: {removing.medicines.join(', ')}. Move them to another time, or keep
                    them at the same clock times every day.
                  </p>
                  <p className="mt-2 text-xs font-semibold text-(--muted)">Move them to:</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {[...MEALS.map((m) => [m, MEAL_NAME[m]] as const), ...times.filter((o) => o.id !== t.id).map((o) => [`time:${o.id}`, o.name] as const)].map(([key, label]) => (
                      <button
                        key={key}
                        disabled={busy}
                        onClick={() => void run(() => remove(t, { medicines: 'move', to: key }))}
                        className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      disabled={busy}
                      onClick={() => void run(() => remove(t, { medicines: 'keep' }))}
                      className="min-h-11 rounded-lg bg-(--accent) px-3 font-medium text-white"
                    >
                      Keep the same clock times
                    </button>
                    <button disabled={busy} onClick={() => setRemoving(null)} className="min-h-11 rounded-lg border border-(--line) bg-(--bg) px-3">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {!canEdit ? null : adding ? (
        <div className="mt-3 rounded-xl border-2 border-(--accent) p-3">
          <div className="flex flex-wrap gap-2">
            <input
              aria-label="Name of the time"
              placeholder="Evening tea"
              value={adding.name}
              maxLength={30}
              autoFocus
              onChange={(e) => setAdding({ ...adding, name: e.target.value })}
              className={`${input} min-w-40 flex-1`}
            />
            <input
              aria-label="Time"
              type="time"
              value={adding.time}
              onChange={(e) => e.target.value && setAdding({ ...adding, time: e.target.value })}
              className={input}
            />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button disabled={busy} onClick={() => setAdding(null)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Cancel</button>
            <button
              disabled={busy || adding.name.trim().length === 0}
              onClick={() => void run(async () => {
                await api.post(`/api/patients/${patientId}/routine-times`, adding);
                setAdding(null);
                await onChanged();
              })}
              className="min-h-11 rounded-lg bg-(--accent) px-3 text-sm font-medium text-white disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>
      ) : times.length >= MAX_OWN ? (
        <p className="mt-3 text-xs text-(--muted)">Up to {MAX_OWN} other times of the day.</p>
      ) : (
        <button
          disabled={busy}
          onClick={() => setAdding({ name: '', time: '17:00' })}
          className="mt-3 min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          + Add a time
        </button>
      )}
    </div>
  );
}
